import type { System } from '@/core/system';
import type { Time } from '@/core/time';
import { City } from './city';
import { BuildingField } from '@/rendering/building-field';
import { SkyLinkField } from '@/rendering/skylink-field';
import { Ground } from '@/rendering/ground';
import { OrnamentField } from '@/rendering/ornament-field';

/**
 * The bridge between the logical city and its GPU representation. Each frame it
 * lets the render fields reconcile with (and animate toward) the current city
 * model. Nothing here decides *what* the city is — only mirrors it. Running last
 * in the frame means it always reflects the evolution engine's mutations from the
 * same tick with zero latency.
 */
export class WorldRenderSystem implements System {
  readonly name = 'world-render';

  constructor(
    private city: City,
    private buildings: BuildingField,
    private links: SkyLinkField,
    private ground: Ground,
    /** Optional so the project compiles before Fable wires the ornament layer in. */
    private ornaments?: OrnamentField,
  ) {}

  update(time: Time): void {
    this.buildings.update(this.city, time.delta, time.elapsed);
    this.links.update(this.city, time.delta);
    this.ornaments?.update(this.city, time.delta, time.elapsed);
    this.ground.update(this.city, time.elapsed);
  }

  dispose(): void {
    this.buildings.dispose();
    this.links.dispose();
    this.ornaments?.dispose();
    this.ground.dispose();
  }
}
